/* @flow */
import { RECEIVE_FOUNDRY_RESPONSE } from '../actions/foundry_response';
import {
  LOAD_LOCALE,
  SET_GEOLOCATION,
  SET_SHOULD_SHOW_GEOLOCATION_BANNER
} from '../actions/i18n';
import type { FooterLink, Newsletter } from '../actions/i18n';
import {
  EDITION_ID_EN_US,
  EDITION_ID_EN_GB,
  EDITION_ID_DE_DE,
  EDITION_ID_EN_CA,
  EDITION_ID_FR_FR,
  EDITION_ID_EN_AU,
  DRAFT_EDITION_IDS
} from '../constants';
import {get, reject} from 'lodash';

/*
 * We can remove this mapping after `edition_data` is populated in all
 * Bridge and Foundry responses.
 */
const SITE_ID_TO_EDITION_ID = {
  us: EDITION_ID_EN_US,
  uk: EDITION_ID_EN_GB,
  de: EDITION_ID_DE_DE,
  ca: EDITION_ID_EN_CA,
  au: EDITION_ID_EN_AU,
  fr: EDITION_ID_FR_FR
};

/*
 * We can remove this mapping after:
 * 1. `edition_data` is nonempty in all Bridge and Foundry responses.
 * 2. the basename is in `edition_data` in Bridge and Foundry.
 */
const SITE_ID_TO_EDITION_BASENAME = {
  us: 'en-us',
  uk: 'en-gb',
  de: 'de-de',
  au: 'en-au',
  ca: 'en-ca',
  fr: 'fr-fr'
};

/*
 * `edition_data` from Bridge and Foundry cannot be relied on yet. To use it in
 * our components right now, we shim it by using the site data.
 *
 * - It is missing the "basename" (this is already a populated column in MySQL)
 * - In some cases it's completely empty. See this doc:
 *   https://docs.google.com/document/d/1Hb4P0r1RFxEK-7bYu8l1TkNiUEQKMXpDgz9WK1UiYN0/edit?usp=sharing
 */
const shimEditionData = (
  editionData: Object = {},
  siteData: Object = {}
): EditionDataState => ({
  ...editionData,
  id: editionData.id || SITE_ID_TO_EDITION_ID[siteData.id],
  basename: editionData.basename || SITE_ID_TO_EDITION_BASENAME[siteData.id],
  domain: editionData.domain || siteData.domain || 'www.refinery29.com',
  config: {
    /*
     * This is used in components but is not present in
     * site_data/edition_data...
     *
     * TODO: See if we can use `pinterest_handle` in these components
     * instead.
     */
    pinterest_link: 'https://www.pinterest.com/refinery29/',

    ...get(editionData, 'config', siteData.options)
  }
});

/**
 * Making sure we exclude any editions that are currently in progress.
 *
 * We do not want to mistakenly link to them from stories/channels
 * in the edition switch or in the alternate links.
 */
const alternateUrls = (action: Object): Array<AlternateUrl> =>
  reject(
    get(action.data, 'page_data.alternate_urls', []),
    ({ edition_id }: AlternateUrl): bool => DRAFT_EDITION_IDS.includes(edition_id)
  );

export default (state: I18nState = {}, action: Object): I18nState => {
  switch (action.type) {
    case RECEIVE_FOUNDRY_RESPONSE:
      return {
        ...state,
        edition_data: shimEditionData(
          action.data.edition_data, action.data.site_data
        ),
        alternate_urls: alternateUrls(action)
      };
    case LOAD_LOCALE:
      return {
        ...state,
        locale: action.locale,
        messages: action.messages,
        footerLinks: action.footerLinks,
        newsletters: action.newsletters
      };
    case SET_GEOLOCATION:
      return {
        ...state,
        geolocation: action.geolocation
      };
    case SET_SHOULD_SHOW_GEOLOCATION_BANNER:
      return {
        ...state,
        shouldShowGeolocationBanner: action.shouldShowGeolocationBanner
      };
    default:
      return state;
  }
};

type I18nState = {
  messages?: { [key: string]: string },
  locale?: string,
  footerLinks?: Array<FooterLink>,
  newsletters?: Array<Newsletter>,
  edition_data?: EditionDataState,
  alternate_urls?: Array<AlternateUrl>,
  geolocation?: string
};

type AlternateUrl = {
  edition_id: number,
  lang: string,
  url: string
};

type EditionDataState = {
  id: string,
  basename: string,
  domain: string,
  config: Object,
  most_popular: Object
};
